<template lang="pug">
	.audio-tracker-track(ref="track",
		:style="trackStyles()",
		@mouseover="e => { tracker.$mouseOverTrack = track; isMouseOver = true; onMouseMove(e); }",
		@mouseleave="e => { tracker.$mouseOverTrack = null; isMouseOver = false; onMouseMove(e); }",
		@mousedown="e => onCanvasMouseDown(e, track)",
		@mouseup="e => onCanvasMouseUp(e, track)",
		@dblclick="onDblClick"
	)
		.track-canvas(
			v-if="track.__supportsResizing || track.__supportsDurationChange",
			ref="canvas", width="0", height="0",
			:class="trackClasses")
			canvas(v-if="false", ref="canvasObj", v-show="false", width="0", height="0")
			.canvas-actions
				.left.resize
					.resizer-handler
				.right.resize
					.resizer-handler
			.canvas-image-holder(v-if="canvasImage && false")
				.canvas-image(:style="{ '--canvas-image': `url(${canvasImage})`, '--leftOffset': `${/**-1 * tracker.display.pixelsPerSecond * (track.startOffset / (track.playbackRate || 1) || 0) +*/ canvasImageLeftOffset}px`, '--width': `${canvasImageWidth}px` }")
			AudioTrackerCanvasImageDrawer(:tracker="tracker", :track="track", :navigatorScrollOffset="navigatorScrollOffset", :navigatorWidth="navigatorWidth", :navigatorHeight="navigatorHeight")
			.track-fade.fade-in(v-if="fadeIn && fadeIn.value", :style="{ width: `${tracker.display.pixelsPerSecond * Math.min(fadeIn.value, track.trimmedDuration - 0.075)}px`}", :class="[fadeIn.type]")
			.track-fade.fade-out(v-if="fadeOut && fadeOut.value", :style="{ width: `${tracker.display.pixelsPerSecond * Math.min(fadeOut.value, track.trimmedDuration - 0.075)}px`}", :class="[fadeOut.type]")
		.break.track-canvas.flex-row.align-center.center(v-else-if="!!track.breakType", ref="canvas",
			:class="[ track.breakType, ...trackClasses ]")
			span {{$Data.secondsToDisplayValue(track.getTrimmedDuration(true))}}
			.canvas-actions
				.left.resize
					.resizer-handler
				.right.resize
					.resizer-handler
		.thumbnails.flex-row
			.track-thumbnail.flex-1(v-for="(thumb, idx) in track.visibleThumbnails", :style="{ '--thumb': `url(${thumb.base64})` }")
		.track-canvas-name-header(v-if="!$scopedSlots['track-name-header']", :title="getTrackNameHeader()") {{subTrackNumber > 0 ? `${subTrackNumber}/${totalSubTracks} ` : ''}}{{getTrackNameHeader()}}
		.track-canvas-name-header
			slot(name="track-name-header", :track="track", :trackName="getTrackNameHeader()", :subTrackNumber="subTrackNumber", :totalSubTracks="totalSubTracks")
		.track-canvas-name(v-if="!$scopedSlots['track-name']", :title="getTrackName()") {{getTrackName()}}
		.track-canvas-name(v-else)
			slot(name="track-name", :track="track", :trackName="getTrackName()")
		.track-canvas-info.flex-row.wrap(v-if="!trackClasses.includes('overlapping') || (!hasSelectedTracks || trackClasses.includes('selected'))")
			.left.flex-row.space-small.align-center.wrap(@mousemove.stop="")
				slot(name="actions-left-static", :track="track")
				slot(v-if="!isActionsVisible", name="actions-left-inactive", :track="track")
				slot(v-if="isActionsVisible", name="actions-left", :track="track", @mouseover="setActionsOver(true)", @mouseleave="setActionsOver(false)")
			.right.flex-row.space-small.align-center.wrap(@mousemove.stop="", @mouseover="setActionsOver(true)", @mouseleave="setActionsOver(false)")
				slot(name="actions-right-static", :track="track")
				slot(v-if="!isActionsVisible", name="actions-right-inactive", :track="track")
				slot(v-if="isActionsVisible", name="actions-right", :track="track")
		.track-borders.break(v-if="!!track.breakType", ref="trackBorder", :class="[ track.breakType ]")
		.track-canvas-loader.flex-row.center.align-center(v-if="!track.__isSrcLoaded || track.__isLoading || track.__isBusy")
			Loader(:text="!track.__isSrcLoaded ? `${track.__loadedPercentage}%` : ''")
		.track-tools
			slot(name="tools")
		.track-marker.flex-row.align-center(v-if="track.__marker")
			.marker-text.nowrap {{track.__marker}}
			.triangle
		Popup(v-if="postEffectsVisible", :popupVisible="postEffectsVisible", classes="tracker-dark", :width="380", :height="420", :closeOnOutsideClick="true", @hiding="v => { postEffectsVisible = false; }")
			.flex-col.space
				AudioTrackInfoPanelPostEffects(v-if="$auth.hasProAudio()", mode="selector", :audioContext="tracker.audioContext", :tracker="tracker", :postEffects="postEffects")
				AudioTrackInfoPanelPostEffects.flex-1(v-if="$auth.hasProAudio()", mode="plugins-list", :audioContext="tracker.audioContext", :tracker="tracker", :postEffects="postEffects")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="showPostEffects(false)")
		EnvelopeControl(v-if="volumeEnvelopeVisible", ref="volumeEnvelopeControl", :track="track", :tracker="tracker", :items="track.insertion.JsonConfig.volumeEnvelope.points", @save="onVolumeEnvelopeSave", :max="2", :min="0", 
			:defNewValue="1", :units="$tCaption('dB', '@actions')", :pencilToolStep="0.025")
		EnvelopeControl(v-if="pitchEnvelopeVisible", ref="pitchEnvelopeControl", :track="track", :tracker="tracker", :items="track.insertion.JsonConfig.pitchEnvelope.points", @save="onPitchEnvelopeSave",
			:min="0.8", :max="1.2", :defNewValue="1", :pencilToolStep="0.01")
		FadeControl(v-if="fadeControlVisible", ref="fadeControl", :track="track", :tracker="tracker")
		slot
		slot(name="canvas", :track="track")
</template>


<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';	
	import DxButton from 'devextreme-vue/button';
	import AudioTrackInfoPanelPostEffects from './AudioTrackInfoPanelPostEffects.vue';
	import Loader from 'app-core/Loader.vue';
	import Popup from 'app-core/Popup.vue';
	import EnvelopeControl from './AudioTrackerEnvelopeControl.vue';
	import AudioTrackerCanvasImageDrawer from './AudioTrackerCanvasImageDrawer.vue';
	import FadeControl from './AudioTrackerFadeControl.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackInfoPanel',
		name: 'audio-track-canvas',
		components: { AudioTrackInfoPanelPostEffects, Loader, Popup, DxButton, EnvelopeControl, AudioTrackerCanvasImageDrawer, FadeControl },
		props: {
			track: VueTypes.any.def({}),
			tracker: VueTypes.any.def(null),
			aggregatedTrack: VueTypes.any.def({}),
			canvasImage: VueTypes.any.def(null),
			subTrackNumber: VueTypes.number.def(null),
			totalSubTracks: VueTypes.number.def(null),
			disabled: VueTypes.any.def(false),
			readonly: VueTypes.any.def(false),
			activeToolId: VueTypes.string.def(null),
			navigatorScrollOffset: VueTypes.any.def(null),
			navigatorWidth: VueTypes.number.def(0),
			navigatorHeight: VueTypes.number.def(0),
		},
		data() {
			return {
				postEffectsVisible: false,
				emitChanges: true,
				isMouseOver: false
			};
		},
		computed: {
			self() {
				return this;
			},
			fadeIn() {
				return this.track?.insertion?.JsonConfig?.fadeIn;
			},
			fadeOut() {
				return this.track?.insertion?.JsonConfig?.fadeOut;
			},
			trackClasses() {
				return [
					this.track.__isMuted ? 'muted' : '',
					this.isMouseOver ? `${this.tracker.activeCanvasMouseOverAction} mouseover` : '',
					this.tracker.selectedTrack?.id === this.track.id || this.tracker.selectedTrackIds?.indexOf(this.track.id) > -1 ? 'selected' : '',
					this.track.__overlappingTracks?.length ? 'overlapping' : ''
				]
			},
			hasSelectedTracks() {
				return this.tracker.selectedTrack?.id || this.tracker.selectedTrackIds?.length;
			},
			sourceTrack() {
				return this.track?.sourceTrack || this.track;
			},
			isActionsVisible() {
				return !this.readonly && (this.isMouseOver || this.tracker.activeTrack?.id === this.track.id);
			},
			postEffects() {
				return this.track.insertion.JsonConfig.postEffects;
			},
			hasPostEffects() {
				return !!this.postEffects?.plugins?.length;
			},
			volumeEnvelopeVisible() {
				return this.activeToolId === 'volumeEnvelope' && this.tracker.toolsTrack?.id === this.track?.id;
			},
			pitchEnvelopeVisible() {
				return this.activeToolId === 'pitchEnvelope' && this.tracker.toolsTrack?.id === this.track?.id;
			},
			fadeControlVisible() {
				return this.activeToolId === 'fadeInOut' && this.tracker.toolsTrack?.id === this.track?.id;
			},
			canvasImageLeftOffset() {
				if (this.navigatorScrollOffset?.left > this.trackStart) {
					return this.navigatorScrollOffset?.left - this.trackStart;
				}
				return 0;
			},
			canvasImageWidth() {
				return Math.min(this.navigatorWidth, this.trackWidth - this.canvasImageLeftOffset);
			},
			trackStart() {
				return this.track?.start * this.tracker?.display?.pixelsPerSecond;
			},
			trackWidth() {
				let width = this.track?.getTrimmedDuration();
				width = width > 0 ? Math.max(width, 0.1) : 5;
				return width * this.tracker?.display?.pixelsPerSecond;
			}
		},
		watch: {
			'track.__isLoading'(v) {
				if (v < 0) {
					this.track.__isLoading = 0;
				}
			},
			'track.id'(v) {
				this.emitChanges = false;
				setTimeout(f => this.emitChange = true, 500);
			},
			'track.insertion.JsonConfig': {
				handler() {
					if (!this.emitChanges) return;
					if (__DEBUG__)
						console.debug('track.insertion.JsonConfig changed');
					let changes = this.track?.__hasChanges();
					if (changes) {
						if (__DEBUG__)
							console.debug('%cJSON CHANGED', 'background: purple; color: white', { changes, track: this.track });
						this.track?.__emitDataChanged('JsonConfig');
					}
				},
				deep: true
			},
			'track.start'() {
				if (!this.emitChanges) return;
				this.track?.__emitDataChanged('start');
			},
			'track.duration'() {
				if (!this.emitChanges) return;
				this.track?.__emitDataChanged('duration');
			},
			'track.startOffset'() {
				if (!this.emitChanges) return;	
				this.track?.__emitDataChanged('startOffset');
			},
			'track.endOffset'() {
				if (!this.emitChanges) return;	
				this.track?.__emitDataChanged('endOffset');
			},
			'track.insertion.EventRule.Oid'() {
				this.$emit('update-tracks');
			},
			'track.insertion.Targets': {
				handler() {
					this.$emit('update-tracks');
				},
				deep: true
			}
		},
		mounted() {
			this.$emit('mounted');
		},
		destroyed() {
			this.$eventsBus.$off('tracker-tool-fade-change', this.onTrackerToolFadeChange);
		},
		methods: {
			trackStyles() {
				let width = this.track?.getTrimmedDuration();
				width = width > 0 ? Math.max(width, 0.1) : 5;
				return {
					left: `${this.trackStart}px`,
					width: `${this.trackWidth}px`,
					height: `${this.track.__canvasHeight}px`,
				};
			},
			setActionsOver(v) {
				this.$actionsOver = v;
			},
			onVolumeEnvelopeSave(points, enabled) {
				if (__DEBUG__)
					console.debug('onVolumeEnvelopeSave', { points, enabled });
				this.track.insertion.JsonConfig.volumeEnvelope = {
					points,
					enabled
				};
			},
			onPitchEnvelopeSave(points, enabled) {
				if (__DEBUG__)
					console.debug('onPitchEnvelopeSave', { points, enabled });
				this.track.insertion.JsonConfig.pitchEnvelope = {
					points,
					enabled
				};
			},
			getTrackName() {
				return typeof(this.track.name) === 'function' ? this.track.name(this.track) : this.track.name;
			},
			getTrackNameHeader() {
				return typeof(this.track.nameHeader) === 'function' ? this.track.nameHeader(this.track) : this.track.nameHeader;
			},
			onTrackClick(e) {
				if (this.disabled) return;
				this.$emit('click', e, this.track);
			},
			onCanvasMouseDown(e, track) {
				if (this.readonly || this.disabled || this.tracker.toolsTrack && this.tracker.checkToolsTrackAction(e, 'mouse-down')) return;				
				this.tracker.activeCanvas = this.$el;
				this.tracker.activeTrackNumberChanged = false;
				this.tracker.activeTrack = track;
				this.tracker.activeTrackMouseDownPosition = [e.clientX, e.clientY];
				this.tracker.$mouseOverAggregatedTrack = track?.__aggregatedTrack?.trackNumber;
				if (!this.$actionsOver) {
					this.tracker.selectedTrack = track?.sourceTrack || track;
					if (this.tracker.selectedTrackIds.indexOf(track.id) === -1) {
						this.tracker.selectedTrackIds = [track.id];
					}
					if (!track?.__isSubTrack)
						this.tracker.selectedMainTrack = this.tracker.selectedTrack;
					
					this.tracker.selectedTracks.map(f => {
						this.tracker.activeTrackInit[f.id] = _.pick(f, ['start', '__trackNumber']);
						f?.subTracks?.map(s => {
							this.tracker.activeTrackInit[s.id] = _.pick(s, ['start', '__trackNumber']);
						});
					});

					track.__initSubTracks();
				}

				if (this.tracker.activeCanvas && !this.tracker.toolsTrack && !this.$actionsOver) {
					this.tracker.activeCanvasAction = this.tracker.activeCanvasMouseOverAction;
					e?.stopPropagation();
					e?.preventDefault();
				}

				this.setActionsOver(false);
			},
			onCanvasMouseUp(e, track) {
				if (this.disabled || this.readonly) return;
				if (this.tracker.selectedTrack?.id !== this.track.id) return;
				if (this.tracker.toolsTrack && this.tracker.checkToolsTrackAction(e, 'mouse-up')) return;
				this.tracker.activeTrackMouseDownPosition = null;

				var trackChanged = this.tracker.activeTrackChanged;
				if (this.tracker.activeTrackChanged) {
					this.tracker.changedTracks[this.tracker.activeTrack.id] = true;
				}
				this.tracker.activeTrackChanged = null;
				this.tracker.activeCanvas = null;
				this.tracker.activeCanvasAction = null;

				if (trackChanged) {
					this.tracker.$emitTracksChanged();
				}
			},
			onMouseMove(e) {
				if (this.disabled || this.readonly) return;
				this.$emit('mousemove', e);
				this.tracker.$mouseOverAggregatedTrack = this.track?.__aggregatedTrack?.trackNumber;
				e?.stopPropagation();
				e?.preventDefault();
			},
			showPostEffects(v) {
				this.postEffectsVisible = false;
				if (v)
					setImmediate(() => {
						this.postEffectsVisible = true;
					})
			},
			onDblClick() {
				this.$emit('dblclick', this.track);
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-track {
		@import "main/themes/generated/variables.base.scss";

		position: absolute;
		top: 0px;
		left: 0px;
		margin: 4px 0px;

		.track-canvas-name {
			position: absolute;
			left: 4px;
			bottom: 2px;
			right: 2px;
			font-size: 10px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			font-weight: 100;
			color: rgba(0,0,0,0.45);
			z-index: 10;
		}

		.track-canvas-name-header {
			position: absolute;
			left: 4px;
			bottom: 14px;
			right: 2px;
			font-size: 8px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			font-weight: 400;
			color: rgb(0 0 0 / 32%);
			z-index: 10;
			text-transform: uppercase;
			font-style: italic;
		}

		.track-canvas-info {
			position: absolute;
			right: 4px;
			top: 4px;
			left: 4px;
			z-index: 10;
			justify-content: space-between;
		}

		.canvas-indicator {
			border-radius: 4px;
			height: 18px;
			font-size: 10px;
			text-align: center;
			opacity: 0.75;
			padding: 0px 4px;
		}

		.variants {
			background: $base-accent;
			color: $base-bg;
		}

		.post-effects {
			background: #999;
			color: $base-bg;

			&:hover {
				background: #333;
			}
		}

		.track-canvas-loader {
			position: absolute;
			width: 100%;
			height: 100%;
			overflow: hidden;
			white-space: nowrap;
			z-index: 101;
			background: rgba(255,255,255,0.15);
			border-radius: 4px;
			pointer-events: none;
		}

		.track-canvas {
			width: 100%;
			height: 100%;

			&.overlapping {
				z-index: 1;
				filter: hue-rotate(190deg);
			}

			.canvas-image-holder {
				position: absolute;
				left: -1px;
				top: -2px;
				right: 0px;
				bottom: 0px;
				overflow: hidden;

				.canvas-image {
					background: var(--canvas-image);
					background-size: 100% 100%;
					position: absolute;
					left: var(--leftOffset);
					top: 0px;
					width: var(--width);
					bottom: 0px;
				}
			}

			.canvas-actions {
				position: relative;
				transition: 0.3s;
				opacity: 0;
				z-index: -1;

				> .resize {
					width: 40px;
					height: 100%;
					position: absolute;
					top: 0px;
					
					.resizer-handler {
						border-radius: 8px;
						width: 15px;
						height: 30%;
						top: 50%;
						transform: translateY(-50%);
						position: absolute;
						//border: 1px solid #cecece;
					}

					&.left {
						left: 4px;

						.resizer-handler {
							right: 0px;
							background: linear-gradient(-90deg, #78787800 0%, #e0e0e0 33%, #b0b0b0 33%, #e0e0e0 53%, #b0b0b0 53%, #e0e0e0 73%, #bebebe 73%, #78787800 100%);
						}
					}

					&.right {
						right: 4px;
						
						.resizer-handler {
							left: 0px;
							background: linear-gradient(90deg, #78787800 0%, #e0e0e0 33%, #b0b0b0 33%, #e0e0e0 53%, #b0b0b0 53%, #e0e0e0 73%, #bebebe 73%, #78787800 100%);
						}
					}
				}
			}

			&.selected {
				.canvas-actions {
					opacity: 1;
				}
			}
		}

		.track-tools {
			z-index: 11;
			position: absolute;
			transform: translateX(100%);
			right: -4px;
			background: #efefefee;
			border-radius: 4px;
			white-space: nowrap;
			border: 1px solid #00000011;

			> * {
				margin: 4px;
			}
		}

		.track-fade {
			position: absolute;
			top: 0px;
			height: 100%;
			background: rgba(0,0,0,0.2);
			z-index: 11;
			pointer-events: none;
			opacity: 0.05;

			&.linear {
				background: url('../../assets/linear-grow.png');
				background-size: 100% 100%;
			}

			&.exponential {
				background: url('../../assets/logarithmic-grow.png');
				background-size: 100% 100%;
			}

			&.fade-in {
				left: 0px;
			}

			&.fade-out {
				right: 0px;
				transform: scaleX(-1);
			}
		}

		.track-marker {
			--marker-color: #00000015;
			position: absolute;
			top: 2px;
			left: 0px;
			pointer-events: none;
			z-index: 100;

			.marker-text {
				background: var(--marker-color);
				font-size: 10px;
				padding: 1px;
				padding-left: 3px;
				color: #000000aa;
			}

			.triangle {
				width: 0;
				height: 0;
				border-width: 8.5px 0 8.5px 4.7px;
				border-color: transparent transparent transparent var(--marker-color);
				border-style: solid;
			}
		}

		.thumbnails {
			position: absolute;
			left: 1px;
			top: 1px;
			right: 1px;
			bottom: 1px;
			z-index: 10;
			overflow: hidden;
			pointer-events: none;
			opacity: 0.5;
			border-radius: 4px;

			.track-thumbnail {
				height: 100%;
				background: var(--thumb);
				background-position: center;
				background-size: cover;
			}
		}
	}
</style>
